import type { InputNode, NodeType } from '../types';
import { parseShopDateValue } from '../nodes/ShopDateSelectNode/utils';

export type StructuredValues = Record<string, string | string[]>;
export type StructuredFiles = Record<string, File | string | null>;

/** key → modules (each carrying a `markdown` string). 用于序列化 `${key}__markdown` 字段（V1 兼容）。 */
export type MarkdownModuleStore = Record<
  string,
  Array<{ title: string; content: string; markdown: string }>
>;

export type BuildFormDataInput = {
  config: InputNode[];
  values: StructuredValues;
  files: StructuredFiles;
  footerFiles: File[];
  /** markdown / markdown-name 解析后产生的 modules，按节点 key 索引 */
  markdownModules?: MarkdownModuleStore;
  /** 被互斥隐藏的节点 key 集合（这些节点不序列化） */
  excludedKeys?: Set<string>;
  /** 被互斥隐藏的 separator 节点索引（按区域精确判定，区域间互不影响） */
  hiddenSeparatorIndexes?: Set<number>;
};

export type BuildFormDataResult = {
  formData: FormData;
  fullPrompt: string;
  hasContent: boolean;
};

const ZERO_WIDTH = /​/g;

const clean = (value: unknown): string =>
  String(value ?? '').replace(ZERO_WIDTH, '').trim();

/** 将 HTML 字符串转为纯文本（对齐 V1 sendHandler 中 el.textContent 取值行为） */
const htmlToText = (html: string): string => {
  if (!html || !/<[a-z][\s\S]*>/i.test(html)) return html;
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return (doc.body.textContent || '').replace(ZERO_WIDTH, '').trim();
};

const isMultiVariableLike = (type: NodeType): boolean =>
  type === 'multi-variable' || type === 'multi-variable-select';

const getStringValue = (values: StructuredValues, key?: string): string => {
  if (!key) return '';
  const v = values[key];
  if (Array.isArray(v)) return clean(v[0]);
  return clean(v);
};

const getArrayValue = (values: StructuredValues, key?: string): string[] => {
  if (!key) return [];
  const v = values[key];
  if (Array.isArray(v)) return v.map(clean).filter(Boolean);
  const single = clean(v);
  return single ? [single] : [];
};

const getFileName = (file: File | string): string => {
  if (typeof file === 'string') {
    return decodeURIComponent(file.split('/').pop()?.split('?')[0] || 'file');
  }
  return file.name;
};

async function urlToFile(url: string): Promise<File | string> {
  const rawName = url.split('/').pop()?.split('?')[0] || 'file';
  const name = decodeURIComponent(rawName);
  try {
    const res = await fetch(`/api/image-proxy?url=${encodeURIComponent(url)}`);
    if (!res.ok) return url;
    const blob = await res.blob();
    return new File([blob], name, { type: blob.type || 'application/octet-stream' });
  } catch {
    return url;
  }
}

const appendValue = async (
  formData: FormData,
  key: string,
  value: unknown,
  repeatArrayKeys: Set<string>,
  // V1 parity（sendHandler.ts:358）：仅当 key 属于 upload 节点时，才把 http(s) URL 字符串
  // 转成 File。report-url 的 url / payload[key] 等非 upload 字段必须保持 URL 字符串原样，
  // 否则 reportUrls 参数会被错误地当成文件格式上传。
  convertUrlToFile: boolean,
): Promise<void> => {
  if (value == null || value === '') return;

  if (value instanceof File) {
    formData.append(key, value);
    return;
  }

  if (convertUrlToFile && typeof value === 'string' && /^https?:\/\//i.test(value)) {
    const resolved = await urlToFile(value);
    formData.append(key, resolved);
    return;
  }

  if (Array.isArray(value)) {
    if (repeatArrayKeys.has(key)) {
      for (const item of value) {
        await appendValue(formData, key, item, repeatArrayKeys, convertUrlToFile);
      }
      return;
    }
    for (let i = 0; i < value.length; i += 1) {
      await appendValue(formData, `${key}[${i}]`, value[i], repeatArrayKeys, convertUrlToFile);
    }
    return;
  }

  formData.append(key, String(value));
};

const SINGLE_VALUE_TYPES: NodeType[] = [
  'variable', 'report-url', 'markdown', 'markdown-name',
  'select', 'searchable-select', 'cascader',
  'textarea', 'richtext',
  'datepicker', 'number-input',
  'video-url',
];

export async function buildFormData(
  input: BuildFormDataInput,
): Promise<BuildFormDataResult> {
  const { config, values, files, footerFiles, markdownModules, excludedKeys, hiddenSeparatorIndexes } = input;
  const payload: Record<string, unknown> = {};
  const promptParts: string[] = [];
  const repeatArrayKeys = new Set<string>(['url']);

  for (let idx = 0; idx < config.length; idx += 1) {
    const node = config[idx];
    const key = node.key;

    // 互斥隐藏的节点（含其文本/上传文件）不序列化（V1 parity）
    if (key && excludedKeys?.has(key)) continue;

    if (node.type === 'text' || node.type === 'tag') {
      // 互斥场景下，被对应区域隐藏的「或」分隔符 text 节点不输出（按索引精确判定）
      if (node.exclusionGroup === 'separator' && hiddenSeparatorIndexes?.has(idx)) continue;
      if (node.text) promptParts.push(node.text);
      continue;
    }

    if (key && isMultiVariableLike(node.type)) {
      const entries = getArrayValue(values, key);
      if (entries.length > 0) {
        payload[key] = entries;
        repeatArrayKeys.add(key);
        promptParts.push(entries.join(' '));
      }
      continue;
    }

    if (key && node.type === 'shop-date-select') {
      const selection = parseShopDateValue(values[key]);
      if (selection) {
        payload.shopId = selection.shopId;
        payload.startDate = selection.startDate;
        payload.endDate = selection.endDate;
        promptParts.push(`${selection.shopName} · ${selection.rangeLabel}`);
      }
      continue;
    }

    if (key && node.type === 'paste-text') {
      const rawValue = values[key];
      const pastedText = Array.isArray(rawValue) ? rawValue[0] : rawValue;
      if (typeof pastedText !== 'string' || pastedText.length === 0) continue;
      payload[key] = pastedText;
      promptParts.push(pastedText);
      continue;
    }

    if (key && SINGLE_VALUE_TYPES.includes(node.type)) {
      let value = getStringValue(values, key);
      // V1 parity（number-input/render.ts:7-9）：value 缺省时回填 defaultNumber，
      // 与渲染层显示的默认值一致，避免用户没动默认值时序列化丢字段。
      if (!value && node.type === 'number-input' && typeof node.defaultNumber === 'number') {
        value = String(node.defaultNumber);
      }
      // select / searchable-select：用户没动默认选项时 values 为空（SelectNode 只读 defaultValue 渲染、
      // 不写回 values）。回填 node.defaultValue，与校验层 isNodeFilled「defaultValue 视为已填」对齐，
      // 否则「校验通过但 FormData 丢字段」。
      if (!value && typeof node.defaultValue === 'string' && node.defaultValue !== '') {
        value = node.defaultValue;
      }
      if (!value) continue;

      payload[key] = value;
      promptParts.push(value);

      if (node.type === 'report-url') {
        const existing = payload.url;
        payload.url =
          existing === undefined
            ? value
            : Array.isArray(existing)
              ? [...existing, value]
              : [String(existing), value];
      }

      if (node.type === 'markdown' || node.type === 'markdown-name') {
        const sourceTextKey = node.sourceTextKey || `${key}__sourceText`;
        payload[sourceTextKey] = value;
      }

      continue;
    }

    if (key && (node.type === 'report-module' || node.type === 'markdown-module')) {
      const moduleKey = `${key}__module_${node.moduleIndex ?? 0}`;
      const rawValue = getStringValue(values, moduleKey) || getStringValue(values, key);
      const value = htmlToText(rawValue);
      if (value) {
        if (!Array.isArray(payload[key])) payload[key] = [];
        (payload[key] as string[]).push(value);
        repeatArrayKeys.add(key);
        promptParts.push(value);
      }
      continue;
    }

    if (key && node.type === 'upload') {
      const isBatch =
        typeof node.maxCount === 'number' &&
        typeof node.minCount === 'number' &&
        node.maxCount > 1;

      if (isBatch) {
        const list: Array<File | string> = [];
        for (let i = 0; i < (node.maxCount ?? 0); i += 1) {
          const f = files[`${key}-${i}`];
          if (f) list.push(f);
        }
        if (list.length > 0) {
          payload[key] = list;
          promptParts.push(` [附带${list.length}个文件: ${list.map(getFileName).join(', ')}] `);
        }
      } else {
        const f = files[key];
        if (f) {
          payload[key] = f;
          promptParts.push(` [附带文件: ${getFileName(f)}] `);
        }
      }
      continue;
    }

    // video-upload / media-upload 节点把原始 File 作为二进制发送，URL 仅用于回显。
    if (key && (node.type === 'video-upload' || node.type === 'media-upload')) {
      const f = files[key];
      if (f) {
        payload[key] = f;
        const mediaLabel = node.type === 'media-upload' ? '音视频' : '视频';
        promptParts.push(` [附带${mediaLabel}: ${getFileName(f)}] `);
      } else {
        const value = getStringValue(values, key);
        if (value) {
          payload[key] = value;
          promptParts.push(value);
        }
      }
      continue;
    }
  }

  const fullPrompt = promptParts.join('').trim();
  const formData = new FormData();
  formData.append('fullPrompt', fullPrompt);

  // V1 parity：只有 upload 节点的 key 才允许把 URL 字符串转成 File（见 appendValue 注释）。
  const uploadKeys = new Set<string>();
  for (const node of config) {
    if (node.type === 'upload' && node.key) uploadKeys.add(node.key);
  }

  // V1 兼容：把 markdown / markdown-name 节点解析后的 modules.markdown 字段，
  // 以重复 key（${nodeKey}__markdown）形式追加到 FormData。
  // - 仅当对应节点 key 在 config 中是 markdown / markdown-name 类型时输出
  // - modules 不存在或全部为空字符串时不输出
  if (markdownModules) {
    for (const node of config) {
      if (!node.key) continue;
      if (node.type !== 'markdown' && node.type !== 'markdown-name') continue;
      const modules = markdownModules[node.key];
      if (!modules || modules.length === 0) continue;
      const markdowns = modules
        .map((m) => (m?.markdown || '').trim())
        .filter((s) => s.length > 0);
      if (markdowns.length === 0) continue;
      const mdKey = `${node.key}__markdown`;
      payload[mdKey] = markdowns;
      repeatArrayKeys.add(mdKey);
    }
  }

  for (const [k, v] of Object.entries(payload)) {
    await appendValue(formData, k, v, repeatArrayKeys, uploadKeys.has(k));
  }

  footerFiles.forEach((file) => {
    formData.append('fileUrls', file);
  });

  return {
    formData,
    fullPrompt,
    hasContent: fullPrompt.length > 0 || footerFiles.length > 0,
  };
}
